import { supabase } from './supabase';
import { Customer } from './customers';

/**
 * Purchase orders — client wrappers over the SECURITY DEFINER RPCs added in
 * 20260806000000..20260806030000. This replaces src/lib/orders.ts (legacy
 * orders/order_items): all money lives in integer cents in the DB, totals are
 * DB-computed, receiving posts stock through receive_purchase_order_items, and
 * supplier bills are recorded as linked expenses (record_purchase_order_bill).
 *
 * The legacy-shaped API at the bottom (createOrder/getOrders/...) mirrors the
 * old orders.ts signatures so existing UI swaps imports without rewrites.
 * Legacy rows were backfilled into purchase_orders, so this reads ONE system.
 */

// ── Native types ─────────────────────────────────────────────

export type PurchaseOrderStatus =
  | 'draft'
  | 'sent'
  | 'partially_received'
  | 'received'
  | 'closed'
  | 'cancelled';

export interface PurchaseOrderLine {
  id: string;
  purchase_order_id: string;
  stock_item_id: string | null;
  description: string;
  unit: string | null;
  quantity: number;
  unit_cost_cents: number;
  subtotal_cents: number;
  quantity_received: number;
  sort_order: number;
  /** Free-text specs/comments for the supplier (e.g. glass measurements). */
  note: string | null;
  /** Reference image URLs for the supplier. */
  images: string[] | null;
  created_at: string;
}

export interface PurchaseOrder {
  id: string;
  business_id: string;
  supplier_id: string | null;
  supplier?: Customer | null;
  project_id: string | null;
  project?: { id: string; title: string } | null;
  // Customer-journey links — set at creation from whatever exists (lead /
  // field quote), booking_id auto-attached by DB triggers on conversion.
  pipeline_opportunities_id: number | null;
  field_quote_id: string | null;
  booking_id: string | null;
  po_number: string | null;
  status: PurchaseOrderStatus;
  currency: string;
  subtotal_cents: number;
  tax_cents: number;
  shipping_cents: number;
  total_cents: number;
  billed_cents: number;
  supplier_snapshot: { name?: string; email?: string; phone?: string; address?: string } | null;
  expected_at: string | null;
  /** Date the supplier has the order ready for pickup (null until set). */
  ready_for_pickup_at: string | null;
  notes: string | null;
  outgoing_email_message_id: string | null;
  outgoing_email_thread_id: string | null;
  sent_at: string | null;
  fully_received_at: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  legacy_order_id: string | null;
  created_at: string;
  updated_at: string;
  lines?: PurchaseOrderLine[];
}

export interface PurchaseOrderReceipt {
  id: string;
  purchase_order_id: string;
  received_at: string;
  notes: string | null;
  created_at: string;
  lines: {
    id: string;
    purchase_order_line_id: string;
    quantity: number;
    unit_cost_cents: number;
  }[];
}

function ensure() {
  if (!supabase) throw new Error('Database connection not available');
  return supabase;
}

export const toCents = (dollars: number): number => Math.round((dollars || 0) * 100);
export const toDollars = (cents: number): number => Math.round(cents || 0) / 100;

// ── Native API ───────────────────────────────────────────────

export interface NewPurchaseOrderLine {
  stockItemId?: string | null;
  description: string;
  unit?: string | null;
  quantity: number;
  unitCostCents: number;
  sortOrder?: number;
  note?: string | null;
  images?: string[] | null;
}

/** Create a draft PO with lines. Returns the PO id (still draft, no number). */
export async function createDraftPurchaseOrder(params: {
  businessId: string;
  supplierId?: string | null;
  projectId?: string | null;
  notes?: string | null;
  expectedAt?: string | null;
  /** CRM lead — pass whatever exists at creation; the DB derives the rest. */
  pipelineOpportunitiesId?: number | null;
  fieldQuoteId?: string | null;
  bookingId?: string | null;
  lines?: NewPurchaseOrderLine[];
}): Promise<string> {
  const sb = ensure();
  const { data: poId, error } = await sb.rpc('create_purchase_order', {
    p_business_id: params.businessId,
    p_supplier_id: params.supplierId ?? null,
    p_project_id: params.projectId ?? null,
    p_notes: params.notes ?? null,
    p_expected_at: params.expectedAt ?? null,
    p_pipeline_opportunities_id: params.pipelineOpportunitiesId ?? null,
    p_field_quote_id: params.fieldQuoteId ?? null,
    p_booking_id: params.bookingId ?? null,
  });
  if (error) throw error;

  for (const [i, line] of (params.lines ?? []).entries()) {
    await addPurchaseOrderLine(poId, { ...line, sortOrder: line.sortOrder ?? i });
  }
  return poId as string;
}

export async function addPurchaseOrderLine(poId: string, line: NewPurchaseOrderLine): Promise<string> {
  const { data, error } = await ensure().rpc('add_purchase_order_line', {
    p_po_id: poId,
    p_description: line.description,
    p_quantity: line.quantity,
    p_unit_cost_cents: line.unitCostCents,
    p_stock_item_id: line.stockItemId ?? null,
    p_unit: line.unit ?? null,
    p_sort_order: line.sortOrder ?? 0,
    p_note: line.note ?? null,
    p_images: line.images?.length ? line.images : null,
  });
  if (error) throw error;
  return data as string;
}

/** Lock the PO, assign its sequential number, snapshot the supplier. Returns the PO number. */
export async function sendPurchaseOrder(poId: string): Promise<string> {
  const { data, error } = await ensure().rpc('send_purchase_order', { p_po_id: poId });
  if (error) throw error;
  return data as string;
}

/** Create + send in one step (the “place order” flow). Returns id + assigned number. */
export async function createAndSendPurchaseOrder(params: {
  businessId: string;
  supplierId?: string | null;
  projectId?: string | null;
  notes?: string | null;
  expectedAt?: string | null;
  pipelineOpportunitiesId?: number | null;
  fieldQuoteId?: string | null;
  bookingId?: string | null;
  lines: NewPurchaseOrderLine[];
}): Promise<{ id: string; poNumber: string }> {
  const id = await createDraftPurchaseOrder(params);
  try {
    const poNumber = await sendPurchaseOrder(id);
    return { id, poNumber };
  } catch (err) {
    // Don't leave a stray draft behind if sending failed validation.
    try { await ensure().rpc('delete_purchase_order', { p_po_id: id }); } catch { /* best effort */ }
    throw err;
  }
}

export async function getPurchaseOrders(
  businessId: string,
  options?: { projectId?: string; supplierId?: string; bookingId?: string; statuses?: PurchaseOrderStatus[] }
): Promise<PurchaseOrder[]> {
  const sb = ensure();
  let query = sb
    .from('purchase_orders')
    .select(`
      *,
      supplier:customers(*),
      project:projects(id, title)
    `)
    .eq('business_id', businessId);

  if (options?.projectId) query = query.eq('project_id', options.projectId);
  if (options?.supplierId) query = query.eq('supplier_id', options.supplierId);
  if (options?.bookingId) query = query.eq('booking_id', options.bookingId);
  if (options?.statuses?.length) query = query.in('status', options.statuses);

  const { data: orders, error } = await query.order('created_at', { ascending: false });
  if (error) throw error;
  if (!orders?.length) return [];

  const ids = orders.map((o) => o.id);
  const { data: lines, error: linesError } = await sb
    .from('purchase_order_lines')
    .select('*')
    .in('purchase_order_id', ids)
    .order('sort_order');
  if (linesError) throw linesError;

  const byPo = (lines || []).reduce<Record<string, PurchaseOrderLine[]>>((acc, l) => {
    (acc[l.purchase_order_id] ||= []).push(l);
    return acc;
  }, {});

  return orders.map((o) => ({ ...o, lines: byPo[o.id] ?? [] }));
}

export async function getPurchaseOrder(poId: string): Promise<PurchaseOrder | null> {
  const sb = ensure();
  const { data, error } = await sb
    .from('purchase_orders')
    .select(`*, supplier:customers(*), project:projects(id, title)`)
    .eq('id', poId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const { data: lines, error: linesError } = await sb
    .from('purchase_order_lines')
    .select('*')
    .eq('purchase_order_id', poId)
    .order('sort_order');
  if (linesError) throw linesError;

  return { ...data, lines: lines ?? [] };
}

/**
 * Record a goods receipt (partial or full). Posts stock_transactions for
 * stock-linked lines and rolls the PO status forward. Quantities are capped at
 * the remaining amount server-side. Returns the receipt id (null if nothing
 * was left to receive).
 */
export async function receivePurchaseOrderItems(
  poId: string,
  lines: { lineId: string; quantity: number; unitCostCents?: number }[],
  opts?: { receivedAt?: string; notes?: string }
): Promise<string | null> {
  const { data, error } = await ensure().rpc('receive_purchase_order_items', {
    p_po_id: poId,
    p_lines: lines.map((l) => ({
      line_id: l.lineId,
      quantity: l.quantity,
      ...(l.unitCostCents !== undefined ? { unit_cost_cents: l.unitCostCents } : {}),
    })),
    p_received_at: opts?.receivedAt ?? new Date().toISOString(),
    p_notes: opts?.notes ?? null,
  });
  if (error) throw error;
  return (data as string | null) ?? null;
}

/** Receive everything still outstanding on the PO. */
export async function receiveAllPurchaseOrderItems(poId: string, notes?: string): Promise<string | null> {
  const po = await getPurchaseOrder(poId);
  if (!po?.lines?.length) return null;
  const pending = po.lines
    .filter((l) => l.quantity_received < l.quantity)
    .map((l) => ({ lineId: l.id, quantity: l.quantity - l.quantity_received }));
  if (!pending.length) return null;
  return receivePurchaseOrderItems(poId, pending, { notes });
}

export async function getPurchaseOrderReceipts(poId: string): Promise<PurchaseOrderReceipt[]> {
  const sb = ensure();
  const { data: receipts, error } = await sb
    .from('purchase_order_receipts')
    .select('*')
    .eq('purchase_order_id', poId)
    .order('received_at', { ascending: false });
  if (error) throw error;
  if (!receipts?.length) return [];

  const { data: lines, error: linesError } = await sb
    .from('purchase_order_receipt_lines')
    .select('*')
    .in('receipt_id', receipts.map((r) => r.id));
  if (linesError) throw linesError;

  return receipts.map((r) => ({
    ...r,
    lines: (lines || []).filter((l) => l.receipt_id === r.id),
  }));
}

export async function updatePurchaseOrderLineCost(lineId: string, unitCostCents: number): Promise<void> {
  const { error } = await ensure().rpc('update_purchase_order_line', {
    p_line_id: lineId,
    p_unit_cost_cents: unitCostCents,
  });
  if (error) throw error;
}

/** Set or clear a line's supplier-facing comment (allowed at any open status). */
export async function updatePurchaseOrderLineNote(lineId: string, note: string | null): Promise<void> {
  const trimmed = (note ?? '').trim();
  const { error } = await ensure().rpc('update_purchase_order_line', {
    p_line_id: lineId,
    ...(trimmed ? { p_note: trimmed } : { p_clear_note: true }),
  });
  if (error) throw error;
}

/** Replace a line's reference images (allowed at any open status). Empty array clears. */
export async function updatePurchaseOrderLineImages(lineId: string, images: string[]): Promise<void> {
  const { error } = await ensure().rpc('update_purchase_order_line', {
    p_line_id: lineId,
    p_images: images,
  });
  if (error) throw error;
}

/**
 * Upload a reference image for a PO line and return its public URL.
 * Uses the same public user-uploads bucket as measurement/booking photos.
 */
export async function uploadPurchaseOrderItemImage(businessId: string, file: File): Promise<string> {
  const sb = ensure();
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `purchase-order-items/${businessId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await sb.storage.from('user-uploads').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;
  return sb.storage.from('user-uploads').getPublicUrl(path).data.publicUrl;
}

export async function deletePurchaseOrderLine(lineId: string): Promise<void> {
  const { error } = await ensure().rpc('delete_purchase_order_line', { p_line_id: lineId });
  if (error) throw error;
}

/**
 * Manually set (or clear) a PO's customer-journey links at any status.
 * Pass whatever was picked — booking, field quote, or lead — and the DB
 * derives the rest, same as at creation. All three links are overwritten.
 */
export async function setPurchaseOrderLinks(
  poId: string,
  links: { pipelineOpportunitiesId?: number | null; fieldQuoteId?: string | null; bookingId?: string | null }
): Promise<void> {
  const { error } = await ensure().rpc('set_purchase_order_links', {
    p_po_id: poId,
    p_pipeline_opportunities_id: links.pipelineOpportunitiesId ?? null,
    p_field_quote_id: links.fieldQuoteId ?? null,
    p_booking_id: links.bookingId ?? null,
  });
  if (error) throw error;
}

export async function cancelPurchaseOrder(poId: string): Promise<void> {
  const { error } = await ensure().rpc('cancel_purchase_order', { p_po_id: poId });
  if (error) throw error;
}

export async function closePurchaseOrder(poId: string): Promise<void> {
  const { error } = await ensure().rpc('close_purchase_order', { p_po_id: poId });
  if (error) throw error;
}

export async function deleteDraftPurchaseOrder(poId: string): Promise<void> {
  const { error } = await ensure().rpc('delete_purchase_order', { p_po_id: poId });
  if (error) throw error;
}

/**
 * Record the supplier's bill against the PO — THIS is what creates the
 * expense (QuickBooks: PO is non-posting; the Bill posts). Amount defaults to
 * the unbilled remainder of the PO total. Returns the new expense id.
 */
export async function recordPurchaseOrderBill(
  poId: string,
  opts?: {
    amountCents?: number;
    expenseDate?: string; // 'YYYY-MM-DD'
    categoryId?: string;
    paymentMethod?: 'cash' | 'credit_card' | 'debit_card' | 'check' | 'bank_transfer' | 'other';
    description?: string;
    notes?: string;
    taxCents?: number;
  }
): Promise<string> {
  const { data, error } = await ensure().rpc('record_purchase_order_bill', {
    p_po_id: poId,
    p_amount_cents: opts?.amountCents ?? null,
    p_expense_date: opts?.expenseDate ?? null,
    p_category_id: opts?.categoryId ?? null,
    p_payment_method: opts?.paymentMethod ?? null,
    p_description: opts?.description ?? null,
    p_notes: opts?.notes ?? null,
    p_tax_cents: opts?.taxCents ?? null,
  });
  if (error) throw error;
  return data as string;
}

/** Bills (expenses) recorded against a PO. */
export async function getPurchaseOrderBills(poId: string): Promise<
  { id: string; amount: number; expense_date: string; description: string; category_id: string | null }[]
> {
  const { data, error } = await ensure()
    .from('expenses')
    .select('id, amount, expense_date, description, category_id')
    .eq('purchase_order_id', poId)
    .order('expense_date', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

// ── Legacy-shaped API (drop-in for src/lib/orders.ts) ────────
// Same names/shapes as the old lib so UI cutover is an import swap. Statuses
// map: sent/partially_received → 'pending'; closed → 'received' for display.

export interface OrderItem {
  id?: string;
  order_id?: string;
  item_id: string;
  quantity: number;
  received_quantity?: number;
  unit_price: number;
  total_price: number;
  item_name: string;
  item_unit: string;
  status?: 'Not Received' | 'Partially Received' | 'Received';
  notes?: string;
  images?: string[];
  created_at?: string;
}

export interface Order {
  id?: string;
  business_id: string;
  supplier_id: string;
  supplier?: Customer;
  order_number: string;
  total_amount: number;
  status: 'pending' | 'received' | 'cancelled';
  project_id?: string | null;
  project?: { id: string; title: string };
  pipeline_opportunities_id?: number | null;
  field_quote_id?: string | null;
  booking_id?: string | null;
  created_at?: string;
  updated_at?: string;
  received_at?: string;
  cancelled_at?: string;
  notes?: string;
  /** Date the order is ready for pickup (yyyy-mm-dd); null/undefined until set. */
  ready_for_pickup_at?: string | null;
  items?: OrderItem[];
  outgoing_email_message_id?: string | null;
  outgoing_email_thread_id?: string | null;
}

function lineToLegacyItem(line: PurchaseOrderLine): OrderItem {
  const received = Math.min(line.quantity_received, line.quantity);
  return {
    id: line.id,
    order_id: line.purchase_order_id,
    item_id: line.stock_item_id ?? '',
    quantity: line.quantity,
    received_quantity: received,
    unit_price: toDollars(line.unit_cost_cents),
    total_price: toDollars(line.subtotal_cents),
    item_name: line.description,
    item_unit: line.unit ?? 'unit',
    status: received <= 0 ? 'Not Received' : received < line.quantity ? 'Partially Received' : 'Received',
    notes: line.note ?? undefined,
    images: line.images ?? undefined,
    created_at: line.created_at,
  };
}

function poToLegacyOrder(po: PurchaseOrder): Order {
  const status: Order['status'] =
    po.status === 'cancelled' ? 'cancelled'
    : po.status === 'received' || po.status === 'closed' ? 'received'
    : 'pending';
  return {
    id: po.id,
    business_id: po.business_id,
    supplier_id: po.supplier_id ?? '',
    supplier: (po.supplier ?? undefined) as Customer | undefined,
    order_number: po.po_number ?? '',
    total_amount: toDollars(po.total_cents),
    status,
    project_id: po.project_id,
    project: po.project ?? undefined,
    pipeline_opportunities_id: po.pipeline_opportunities_id,
    field_quote_id: po.field_quote_id,
    booking_id: po.booking_id,
    created_at: po.created_at,
    updated_at: po.updated_at,
    received_at: po.fully_received_at ?? undefined,
    cancelled_at: po.cancelled_at ?? undefined,
    notes: po.notes ?? undefined,
    ready_for_pickup_at: po.ready_for_pickup_at ?? null,
    items: (po.lines ?? []).map(lineToLegacyItem),
    outgoing_email_message_id: po.outgoing_email_message_id,
    outgoing_email_thread_id: po.outgoing_email_thread_id,
  };
}

/** Set or clear the ready-for-pickup date on a PO (non-financial metadata). */
export async function setPurchaseOrderReadyForPickup(
  poId: string,
  readyForPickupAt: string | null
): Promise<void> {
  const { error } = await ensure()
    .from('purchase_orders')
    .update({
      ready_for_pickup_at: readyForPickupAt,
      updated_at: new Date().toISOString(),
    })
    .eq('id', poId);
  if (error) throw error;
}

/** Create + send a PO. The DB assigns the number — read it from the returned order_number. */
export async function createOrder(order: Order, orderItems: OrderItem[]): Promise<Order> {
  const { id, poNumber } = await createAndSendPurchaseOrder({
    businessId: order.business_id,
    supplierId: order.supplier_id || null,
    projectId: order.project_id || null,
    pipelineOpportunitiesId: order.pipeline_opportunities_id ?? null,
    fieldQuoteId: order.field_quote_id ?? null,
    bookingId: order.booking_id ?? null,
    notes: order.notes || null,
    lines: orderItems.map((item, i) => ({
      stockItemId: item.item_id || null,
      description: item.item_name,
      unit: item.item_unit,
      quantity: item.quantity,
      unitCostCents: toCents(item.unit_price),
      sortOrder: i,
      note: item.notes || null,
      images: item.images?.length ? item.images : null,
    })),
  });
  return { ...order, id, order_number: poNumber, items: orderItems };
}

export async function getOrders(
  businessId: string,
  options?: { projectId?: string; supplierId?: string }
): Promise<Order[]> {
  const pos = await getPurchaseOrders(businessId, {
    projectId: options?.projectId,
    supplierId: options?.supplierId,
    // Drafts are excluded from the legacy list (legacy POs were sent at creation).
    statuses: ['sent', 'partially_received', 'received', 'closed', 'cancelled'],
  });
  return pos.map(poToLegacyOrder);
}

export interface BookingPurchaseOrderSummary {
  orders: Order[];
  /** Total units ordered across all POs linked to the booking (cancelled excluded). */
  totalQuantity: number;
  receivedQuantity: number;
  allReceived: boolean;
}

/** POs that follow the customer to this booking, with a received-materials rollup. */
export async function getBookingPurchaseOrderSummary(
  businessId: string,
  bookingId: string
): Promise<BookingPurchaseOrderSummary> {
  const pos = await getPurchaseOrders(businessId, {
    bookingId,
    statuses: ['sent', 'partially_received', 'received', 'closed'],
  });
  const orders = pos.map(poToLegacyOrder);
  let totalQuantity = 0;
  let receivedQuantity = 0;
  for (const o of orders) {
    for (const item of o.items ?? []) {
      totalQuantity += item.quantity;
      receivedQuantity += Math.min(item.received_quantity ?? 0, item.quantity);
    }
  }
  return {
    orders,
    totalQuantity,
    receivedQuantity,
    allReceived: totalQuantity > 0 && receivedQuantity >= totalQuantity,
  };
}

export async function updateOrderStatus(
  orderId: string,
  status: 'pending' | 'received' | 'cancelled'
): Promise<Order | null> {
  if (status === 'cancelled') {
    await cancelPurchaseOrder(orderId);
  } else if (status === 'received') {
    await receiveAllPurchaseOrderItems(orderId);
  } else {
    throw new Error('A sent purchase order cannot be reverted to pending');
  }
  const po = await getPurchaseOrder(orderId);
  return po ? poToLegacyOrder(po) : null;
}

/**
 * Legacy semantics: receivedQuantity is CUMULATIVE. We receive the positive
 * delta through the receipt RPC (which posts stock). Un-receiving isn't
 * supported in the new system — inventory already moved.
 */
export async function updateOrderItemStatus(
  itemId: string,
  status: 'Not Received' | 'Partially Received' | 'Received',
  receivedQuantity?: number
): Promise<OrderItem | null> {
  const sb = ensure();
  const { data: line, error } = await sb
    .from('purchase_order_lines')
    .select('*')
    .eq('id', itemId)
    .single();
  if (error) throw error;
  if (!line) throw new Error('Order item not found');

  let target = line.quantity_received;
  if (status === 'Received') target = line.quantity;
  else if (status === 'Partially Received' && receivedQuantity !== undefined) {
    target = Math.min(line.quantity, receivedQuantity);
  } else if (status === 'Not Received') target = 0;

  const delta = target - line.quantity_received;
  if (delta < 0) {
    throw new Error('Un-receiving items is not supported — stock has already been updated. Adjust inventory from the Stock app instead.');
  }
  if (delta > 0) {
    await receivePurchaseOrderItems(line.purchase_order_id, [{ lineId: itemId, quantity: delta }]);
  }

  const { data: updated } = await sb
    .from('purchase_order_lines')
    .select('*')
    .eq('id', itemId)
    .single();
  return updated ? lineToLegacyItem(updated) : null;
}

export async function markAllItemsAsReceived(orderId: string): Promise<OrderItem[]> {
  await receiveAllPurchaseOrderItems(orderId);
  const po = await getPurchaseOrder(orderId);
  return (po?.lines ?? []).map(lineToLegacyItem);
}

export async function removeOrderItem(orderItemId: string): Promise<void> {
  await deletePurchaseOrderLine(orderItemId);
}

export async function updateOrderItemPrice(orderItemId: string, unitPrice: number): Promise<void> {
  await updatePurchaseOrderLineCost(orderItemId, toCents(unitPrice));
}

/**
 * Sent POs are cancelled (kept for audit) rather than deleted; only drafts
 * are physically deleted. Matches accounting expectations for QB sync.
 */
export async function deleteOrder(orderId: string): Promise<{ success: boolean }> {
  const po = await getPurchaseOrder(orderId);
  if (!po) return { success: true };
  if (po.status === 'draft') {
    await deleteDraftPurchaseOrder(orderId);
  } else {
    await cancelPurchaseOrder(orderId);
  }
  return { success: true };
}

export async function getOnOrderQuantities(businessId: string): Promise<Record<string, number>> {
  const sb = ensure();
  const { data, error } = await sb
    .from('purchase_order_lines')
    .select(`
      stock_item_id,
      quantity,
      quantity_received,
      purchase_orders!inner(business_id, status)
    `)
    .eq('purchase_orders.business_id', businessId)
    .in('purchase_orders.status', ['sent', 'partially_received']);
  if (error) {
    console.error('Error fetching on order quantities:', error);
    return {};
  }

  const out: Record<string, number> = {};
  for (const line of data ?? []) {
    if (!line.stock_item_id) continue;
    const pending = line.quantity - (line.quantity_received || 0);
    if (pending > 0) out[line.stock_item_id] = (out[line.stock_item_id] || 0) + pending;
  }
  return out;
}

export async function getPendingOrdersForItems(
  businessId: string
): Promise<{ itemId: string; quantityOrdered: number; orderNumbers: string[] }[]> {
  const sb = ensure();
  const { data, error } = await sb
    .from('purchase_order_lines')
    .select(`
      stock_item_id,
      quantity,
      quantity_received,
      purchase_orders!inner(business_id, status, po_number)
    `)
    .eq('purchase_orders.business_id', businessId)
    .in('purchase_orders.status', ['sent', 'partially_received']);
  if (error) throw error;

  const map = new Map<string, { quantity: number; orderNumbers: Set<string> }>();
  for (const line of data ?? []) {
    if (!line.stock_item_id) continue;
    const pending = line.quantity - (line.quantity_received || 0);
    if (pending <= 0) continue;
    const poNumber = (line.purchase_orders as any)?.po_number;
    const existing = map.get(line.stock_item_id);
    if (existing) {
      existing.quantity += pending;
      if (poNumber) existing.orderNumbers.add(poNumber);
    } else {
      map.set(line.stock_item_id, {
        quantity: pending,
        orderNumbers: new Set(poNumber ? [poNumber] : []),
      });
    }
  }

  return Array.from(map.entries()).map(([itemId, v]) => ({
    itemId,
    quantityOrdered: v.quantity,
    orderNumbers: Array.from(v.orderNumbers),
  }));
}

/** Backorder split: unreceived remainder moves to a new PO (sent + numbered). */
export async function createNewPOFromItem(
  _originalOrderId: string,
  orderItemId: string,
  _businessId: string
): Promise<{ success: boolean; newOrderNumber?: string; error?: string }> {
  try {
    const sb = ensure();
    const { data: newPoId, error } = await sb.rpc('split_purchase_order_line_backorder', {
      p_line_id: orderItemId,
    });
    if (error) throw error;
    const newOrderNumber = await sendPurchaseOrder(newPoId as string);
    return { success: true, newOrderNumber };
  } catch (error) {
    console.error('Error creating new PO from item:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create new PO',
    };
  }
}

// ── Email threading (order_email_threads on purchase_order_id) ──

export async function updateOrderEmailInfo(
  orderId: string,
  messageId: string,
  threadId: string
): Promise<void> {
  const { error } = await ensure()
    .from('purchase_orders')
    .update({
      outgoing_email_message_id: messageId,
      outgoing_email_thread_id: threadId,
      updated_at: new Date().toISOString(),
    })
    .eq('id', orderId);
  if (error) throw error;
}

export async function linkEmailToOrder(
  orderId: string,
  businessId: string,
  supplierId: string,
  supplierEmail: string,
  subject: string,
  content: string,
  messageId: string
): Promise<void> {
  const sb = ensure();
  try {
    const { data: allMessagesRecord } = await sb
      .from('all_messages')
      .select('id')
      .eq('business_id', businessId)
      .eq('direction', 'outbound')
      .eq('message_type', 'email')
      .eq('external_id', messageId)
      .limit(1)
      .maybeSingle();

    const { error: insertError } = await sb.from('order_email_threads').insert({
      purchase_order_id: orderId,
      order_id: null,
      business_id: businessId,
      supplier_id: supplierId,
      all_messages_id: allMessagesRecord?.id || null,
      external_id: messageId,
      email_address: supplierEmail,
      direction: 'outbound',
      subject,
      content,
      body_html: content,
      sent_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
    });
    if (insertError) console.error('Error storing order email thread entry:', insertError);
  } catch (error) {
    console.error('Error linking email to order:', error);
  }
}

export async function getOrderEmailThread(
  businessId: string,
  orderId: string
): Promise<Array<{
  id: string;
  direction: 'outbound' | 'inbound';
  subject: string | null;
  content: string;
  body_html: string | null;
  email_address: string;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
  external_id?: string | null;
  read?: 'read' | 'unread' | null;
  all_messages_id?: string | null;
}>> {
  const sb = ensure();
  // Backfilled threads carry both ids; new threads only purchase_order_id.
  const { data: threadData, error } = await sb
    .from('order_email_threads')
    .select('id, direction, subject, content, body_html, email_address, sent_at, received_at, created_at, external_id, all_messages_id')
    .eq('business_id', businessId)
    .or(`purchase_order_id.eq.${orderId},order_id.eq.${orderId}`)
    .order('created_at', { ascending: true });
  if (error) throw error;

  type ThreadEntry = {
    id: string;
    direction: 'outbound' | 'inbound';
    subject: string | null;
    content: string;
    body_html: string | null;
    email_address: string;
    sent_at: string | null;
    received_at: string | null;
    created_at: string;
    external_id?: string | null;
    read?: 'read' | 'unread' | null;
    all_messages_id?: string | null;
  };

  const rows = threadData || [];
  const allMessagesIds = rows
    .map((e) => e.all_messages_id)
    .filter((id): id is string => id !== null);
  const externalIds = rows
    .filter((e) => !e.all_messages_id && e.external_id)
    .map((e) => e.external_id as string);

  let readById: Record<string, 'read' | 'unread'> = {};
  if (allMessagesIds.length > 0) {
    const { data } = await sb.from('all_messages').select('id, read').in('id', allMessagesIds);
    readById = (data || []).reduce((acc, m) => { acc[m.id] = m.read || 'unread'; return acc; }, {} as Record<string, 'read' | 'unread'>);
  }

  let readByExternal: Record<string, 'read' | 'unread'> = {};
  if (externalIds.length > 0) {
    const { data } = await sb.from('all_messages').select('twilio_message_sid, read').in('twilio_message_sid', externalIds);
    readByExternal = (data || []).reduce((acc, m) => {
      if (m.twilio_message_sid) acc[m.twilio_message_sid] = m.read || 'unread';
      return acc;
    }, {} as Record<string, 'read' | 'unread'>);
  }

  const mapped: ThreadEntry[] = rows.map((email: any) => ({
    ...email,
    read:
      (email.all_messages_id && readById[email.all_messages_id]) ||
      (email.external_id && readByExternal[email.external_id]) ||
      'unread',
  }));

  // Recover the original outbound PO email when it was never written to
  // order_email_threads (common for the inventory Place Order path).
  const { data: po } = await sb
    .from('purchase_orders')
    .select('id, supplier_id, po_number, outgoing_email_message_id, created_at, supplier:customers(email)')
    .eq('id', orderId)
    .maybeSingle();

  const outgoingMsgId = po?.outgoing_email_message_id || null;
  const hasOriginalOutbound = outgoingMsgId
    ? mapped.some((e) => e.direction === 'outbound' && e.external_id === outgoingMsgId)
    : mapped.some((e) => e.direction === 'outbound');

  if (po && !hasOriginalOutbound) {
    let outboundMsg: {
      id: string;
      subject: string | null;
      content: string | null;
      body: string | null;
      to_number: string | null;
      email_address?: string | null;
      created_at: string;
      twilio_message_sid: string | null;
      external_id?: string | null;
    } | null = null;

    if (outgoingMsgId) {
      const { data: bySid } = await sb
        .from('all_messages')
        .select('id, subject, content, body, to_number, email_address, created_at, twilio_message_sid, external_id')
        .eq('business_id', businessId)
        .eq('direction', 'outbound')
        .eq('message_type', 'email')
        .eq('twilio_message_sid', outgoingMsgId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      outboundMsg = bySid;
      if (!outboundMsg) {
        const { data: byExt } = await sb
          .from('all_messages')
          .select('id, subject, content, body, to_number, email_address, created_at, twilio_message_sid, external_id')
          .eq('business_id', businessId)
          .eq('direction', 'outbound')
          .eq('message_type', 'email')
          .eq('external_id', outgoingMsgId)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        outboundMsg = byExt;
      }
    }

    // Fallback: match by [ORDER-PO-…] subject when outgoing ids were never stamped.
    if (!outboundMsg && po.po_number) {
      const { data } = await sb
        .from('all_messages')
        .select('id, subject, content, body, to_number, email_address, created_at, twilio_message_sid, external_id')
        .eq('business_id', businessId)
        .eq('direction', 'outbound')
        .eq('message_type', 'email')
        .ilike('subject', `%[ORDER-${po.po_number}]%`)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      outboundMsg = data;
    }

    if (outboundMsg || outgoingMsgId) {
      const supplierRow = Array.isArray(po.supplier) ? po.supplier[0] : po.supplier;
      const supplierEmail =
        (supplierRow as { email?: string | null } | null)?.email ||
        outboundMsg?.to_number ||
        outboundMsg?.email_address ||
        '';
      const messageId = outboundMsg?.twilio_message_sid || outboundMsg?.external_id || outgoingMsgId!;
      const body = outboundMsg?.body || outboundMsg?.content || '';
      const subject =
        outboundMsg?.subject ||
        (po.po_number ? `[ORDER-${po.po_number}] Purchase Order` : 'Purchase Order');
      const createdAt = outboundMsg?.created_at || po.created_at || new Date().toISOString();

      // Persist so the next open doesn't need recovery (and replies stay attached).
      if (po.supplier_id && supplierEmail && messageId) {
        try {
          if (!outgoingMsgId) {
            await updateOrderEmailInfo(orderId, messageId, messageId);
          }
          await linkEmailToOrder(
            orderId,
            businessId,
            po.supplier_id,
            supplierEmail,
            subject,
            body,
            messageId
          );
        } catch (err) {
          console.warn('Could not persist recovered PO outbound email:', err);
        }
      }

      mapped.push({
        id: outboundMsg?.id || `recovered-outbound-${orderId}`,
        direction: 'outbound',
        subject,
        content: body,
        body_html: body || null,
        email_address: supplierEmail,
        sent_at: createdAt,
        received_at: null,
        created_at: createdAt,
        external_id: messageId,
        read: 'read',
        all_messages_id: outboundMsg?.id || null,
      });
    }
  }

  return mapped.sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

export async function getUnreadCountForOrder(businessId: string, orderId: string): Promise<number> {
  const sb = ensure();
  try {
    const { data: threadData, error } = await sb
      .from('order_email_threads')
      .select('all_messages_id, external_id')
      .eq('business_id', businessId)
      .or(`purchase_order_id.eq.${orderId},order_id.eq.${orderId}`)
      .eq('direction', 'inbound');
    if (error) throw error;
    if (!threadData?.length) return 0;

    const allMessagesIds = threadData
      .map((e) => e.all_messages_id)
      .filter((id): id is string => id !== null);
    const externalIds = threadData
      .filter((e) => e.all_messages_id === null && e.external_id !== null)
      .map((e) => e.external_id as string);

    let count = 0;
    if (allMessagesIds.length > 0) {
      const { count: c } = await sb
        .from('all_messages')
        .select('*', { count: 'exact', head: true })
        .in('id', allMessagesIds)
        .eq('read', 'unread');
      count += c || 0;
    }
    if (externalIds.length > 0) {
      const { count: c } = await sb
        .from('all_messages')
        .select('*', { count: 'exact', head: true })
        .in('twilio_message_sid', externalIds)
        .eq('read', 'unread');
      count += c || 0;
    }
    return count;
  } catch (error) {
    console.error('Error fetching unread count for order:', error);
    return 0;
  }
}

/**
 * Batched unread supplier-reply counts for many orders at once (3 queries
 * total instead of 2 per order). Used by the pinned orders widget.
 */
export async function getUnreadCountsForOrders(
  businessId: string,
  orderIds: string[]
): Promise<Record<string, number>> {
  const sb = ensure();
  const counts: Record<string, number> = {};
  if (!orderIds.length) return counts;

  try {
    const idList = orderIds.join(',');
    const { data: threadData, error } = await sb
      .from('order_email_threads')
      .select('purchase_order_id, order_id, all_messages_id, external_id')
      .eq('business_id', businessId)
      .eq('direction', 'inbound')
      .or(`purchase_order_id.in.(${idList}),order_id.in.(${idList})`);
    if (error) throw error;
    if (!threadData?.length) return counts;

    const allMessagesIds = [...new Set(
      threadData.map((e) => e.all_messages_id).filter((id): id is string => id !== null)
    )];
    const externalIds = [...new Set(
      threadData
        .filter((e) => e.all_messages_id === null && e.external_id !== null)
        .map((e) => e.external_id as string)
    )];

    const unreadByMessageId = new Set<string>();
    const unreadByExternalId = new Set<string>();
    if (allMessagesIds.length > 0) {
      const { data } = await sb
        .from('all_messages')
        .select('id')
        .in('id', allMessagesIds)
        .eq('read', 'unread');
      (data || []).forEach((m) => unreadByMessageId.add(m.id));
    }
    if (externalIds.length > 0) {
      const { data } = await sb
        .from('all_messages')
        .select('twilio_message_sid')
        .in('twilio_message_sid', externalIds)
        .eq('read', 'unread');
      (data || []).forEach((m) => { if (m.twilio_message_sid) unreadByExternalId.add(m.twilio_message_sid); });
    }

    const wanted = new Set(orderIds);
    for (const entry of threadData) {
      const key = [entry.purchase_order_id, entry.order_id].find((id) => id && wanted.has(id));
      if (!key) continue;
      const isUnread = entry.all_messages_id
        ? unreadByMessageId.has(entry.all_messages_id)
        : entry.external_id
          ? unreadByExternalId.has(entry.external_id)
          : false;
      if (isUnread) counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  } catch (error) {
    console.error('Error fetching unread counts for orders:', error);
    return counts;
  }
}
